import { useCallback } from "react";
import { useRouter } from "next/router";
import { getExamGate } from "@/fetcher/getExamGate";
import type { ExamType } from "@/fetcher/teacher/exam/types";
import { hasEnteredExam } from "@/utils/examEntry";

/**
 * 검사 챕터 시작 게이트 훅.
 *
 * 학생이 검사용 챕터를 풀기 시작하는 진입 지점(홈 TodayProblem "시작")에서 목적지로
 * 이동하기 직전 호출한다. 하나의 진입 함수(`startChapter`)로 여러 진입 지점을 묶어
 * 중복 구현을 막는다.
 *
 * 흐름:
 *  1) `GET /app/exam/gate` 로 이 챕터 묶음이 코드 입장이 필요한지 판정.
 *  2) requiresCode && 미입장(sessionStorage) → `/exam/enter?next=<목적지>` 로 강제 이동.
 *  3) 그 외(불필요 / 이미 입장 / 판정 실패) → 원래 목적지로 진입.
 *
 * ★ examRef 미확정(BE preview 응답이 chapterSetId/essayChapterTemplateId 를 아직
 *   노출하지 않는 과도기) → 게이트를 스킵하고 기존 흐름대로 진입한다(fail-open).
 *   전면 차단(fail-closed)은 검사 아닌 일반 챕터까지 잠가 회귀를 유발하므로 채택 안 함.
 *   BE 가 preview 에 examRef 를 노출하면 이 훅 변경 없이 게이트가 자동 발동한다.
 * ★ gate 조회 실패(네트워크/5xx)도 fail-open. 학습 진입 자체를 막지 않는다.
 */
export interface StartChapterTarget {
  examType: ExamType;
  /** OBJECTIVE=chapterSetId / ESSAY=essayChapterTemplateId. 미확정이면 null/undefined. */
  examRef: number | null | undefined;
  /** 게이트 통과 시 이동할 내부 경로(쿼리 포함). 예: `/problem`, `/essay-learning/play?chapterId=12`. */
  destination: string;
  /**
   * 이동 전에 전체 화면을 요청할지. 브라우저는 사용자 클릭 안에서만 전체 화면을 허용하므로
   * 풀이 화면이 스스로 요청할 수 없다. 홈 카드 클릭(여기)이 그 클릭이다.
   * 논서술형 재접속에서 "전체 화면으로 돌아온다"를 지키기 위한 자리. 실패해도 이동은 계속한다.
   */
  fullscreen?: boolean;
}

/** 게이트 판정 후 라우팅까지 처리하는 훅 본체. 판정 로직과 정책은 파일 상단 설명 참고. */
export const useExamEntryGate = () => {
  const router = useRouter();

  const startChapter = useCallback(
    async (target: StartChapterTarget): Promise<void> => {
      const { examType, examRef, destination, fullscreen } = target;

      // 클릭 제스처가 살아 있는 지금 요청해야 한다. await 뒤(게이트 조회 후)에는 제스처가 끝나 거부된다.
      if (
        fullscreen &&
        typeof document !== "undefined" &&
        !document.fullscreenElement
      ) {
        void document.documentElement.requestFullscreen().catch(() => undefined);
      }

      // examRef 미확정 → 게이트 판정 불가 → 기존 흐름대로(fail-open).
      if (examRef == null) {
        await router.push(destination);
        return;
      }

      try {
        const gate = await getExamGate(examType, examRef);
        if (
          gate.requiresCode &&
          gate.examId != null &&
          !hasEnteredExam(gate.examId)
        ) {
          await router.push({
            pathname: "/exam/enter",
            query: { next: destination },
          });
          return;
        }
      } catch {
        // 게이트 조회 실패 시 학습 진입을 막지 않는다(fail-open).
      }

      await router.push(destination);
    },
    [router],
  );

  return { startChapter };
};

export default useExamEntryGate;
